import { AxiosError, AxiosResponse } from 'axios';
import * as Sentry from '@sentry/react';

import { RESPONSE_CODES } from '../constants/responseCodes.constants';
import { api, isRequestCanceled } from '../http';
import { store, resetAllStores } from '../store';
import { queryClient } from './react-query/react-query.client';
import {
    DefaultRes,
    SystemUserTypes,
    DefaultCallback,
    LocationAddress,
} from '../types';
import { AuthTypes } from '../types/auth.types';
import { CustomerService } from './customer.service';
import { startAuthenticatedReplay } from './sentry-replay.service';

export class AuthService {
    public static readonly dispatch = store.dispatch.auth;

    public static async setPushToken(pushToken: string) {
        await api.post('/customerWebAppUser/setPushToken', { pushToken });
    }

    /** Records terms acceptance and returns { termsAcceptedAt, termsAcceptedVersion }. */
    public static async acceptTerms(version: string) {
        const { data } = await api.post<DefaultRes<{ termsAcceptedAt: string; termsAcceptedVersion: string }>>(
            '/customerWebAppUser/acceptTerms',
            { version },
        );
        return data.result;
    }

    public static async whoIam(signal?: AbortSignal) {
        const {
            dispatch: { updateUser },
        } = AuthService;

        try {
            const {
                data: { isValid, result },
            } = await api.get<
                DefaultRes<SystemUserTypes.Model>,
                AxiosResponse<DefaultRes<SystemUserTypes.Model>>
            >(`/auth/customer/whoIam`, { signal });

            if (signal?.aborted) {
                return;
            }

            if (isValid) {
                updateUser(result);
                Sentry.setUser({ id: result.id, email: result.email, username: result.fullname });
                startAuthenticatedReplay();
                // Refresh customer details alongside user refresh
                const details = await CustomerService.getCustomerDetails(undefined, signal);

                if (signal?.aborted) {
                    return;
                }

                // Restore selectedSubscription from fetched details (needed after page refresh)
                if (details?.subscriptions?.[0]) {
                    store.dispatch.customer.setSelectedSubscription(details.subscriptions[0]);
                }

                // Feature-flag visibility gates read useEffectiveSettings
                // (GET /v2/settings/me); the legacy customer/getSettings
                // bootstrap fetch that used to live here fed a redux slice
                // nothing reads anymore.
            }
        } catch (error: any) {
            if (isRequestCanceled(error)) {
                return;
            }
            throw error;
        }
    }

    public static async login(
        email: string,
        password: string,
        callback?: (isValid?: boolean, message?: string) => void
    ) {
        const {
            dispatch: { toggleLoading, auth },
        } = AuthService;

        try {
            toggleLoading('login', true);

            const {
                data: { isValid, message, result },
            } = await api.post<
                AuthTypes.LoginResponse,
                AxiosResponse<AuthTypes.LoginResponse>
            >(`/auth/login`, { email, password });

            toggleLoading('login', false);

            if (isValid) {
                // A new identity is taking over this tab: drop every org-scoped
                // cache the previous session left behind (same convention as the
                // masquerade flow). Without this, v2-effective-me serves the
                // PREVIOUS org's settings to the new user for its stale window —
                // e.g. a POM Master logging in after a standalone-org session
                // was shown the standalone terms gate.
                resetAllStores();
                queryClient.clear();
                auth(result?.user, result?.token);
                Sentry.setUser({ id: result?.user?.id, email: result?.user?.email, username: result?.user?.fullname });
                startAuthenticatedReplay();

                if (result.subscription) {
                    store.dispatch.customer.setSelectedSubscription(
                        result.subscription
                    );
                }
            }

            return callback && callback(isValid, RESPONSE_CODES[message]);
        } catch (error: any) {
            toggleLoading('login', false);
            if (error.response) {
                const ERROR_CODE = (
                    error as AxiosError<AuthTypes.LoginResponse>
                ).response?.data?.message;

                return callback && callback(false, RESPONSE_CODES[ERROR_CODE!]);
            }
            return callback && callback(false, String(error));
        }
    }

    public static async sso(
        email: string,
        token: string,
        name: string,
        provider: string,
        callback?: (isValid?: boolean, message?: string) => void,
        signal?: AbortSignal
    ) {
        const {
            dispatch: { toggleLoading, auth },
        } = AuthService;

        try {
            toggleLoading('login', true);

            const {
                data: { isValid, message, result },
            } = await api.post<
                DefaultRes<SystemUserTypes.Model>,
                AxiosResponse<
                    DefaultRes<{ user: SystemUserTypes.Model; token: string }>
                >
            >(`/auth/sso`, { email, token, name, provider }, { signal });

            toggleLoading('login', false);

            if (isValid) {
                // Same identity-change cache reset as login above.
                resetAllStores();
                queryClient.clear();
                auth(result?.user, result?.token);
                Sentry.setUser({ id: result?.user?.id, email: result?.user?.email, username: result?.user?.fullname });
                startAuthenticatedReplay();
            }

            return callback && callback(isValid, RESPONSE_CODES[message]);
        } catch (error: any) {
            toggleLoading('login', false);
            if (isRequestCanceled(error)) {
                return;
            }
            if (error.response) {
                const ERROR_CODE = (
                    error as AxiosError<DefaultRes<SystemUserTypes.Model>>
                ).response?.data?.message;

                return callback && callback(false, RESPONSE_CODES[ERROR_CODE!]);
            }
            return callback && callback(false, String(error));
        }
    }

    public static async recoverPassword(
        email: string,
        callback: DefaultCallback
    ) {
        try {
            const {
                data: { isValid, message },
            } = await api.post<
                DefaultRes<boolean>,
                AxiosResponse<DefaultRes<boolean>>
            >(`/auth/customer-recover-password`, { email });

            return callback && callback(isValid, RESPONSE_CODES[message]);
        } catch (error: any) {
            if (error.response) {
                const ERROR_CODE = (error as AxiosError<DefaultRes<boolean>>)
                    .response?.data?.message;

                return callback && callback(false, RESPONSE_CODES[ERROR_CODE!]);
            }
            return callback && callback(false, String(error));
        }
    }

    public static async resetPassword(
        id: string,
        password: string,
        callback: DefaultCallback
    ) {
        try {
            const {
                data: { isValid, message },
            } = await api.post<
                DefaultRes<boolean>,
                AxiosResponse<DefaultRes<boolean>>
            >(`/auth/customer-reset-password`, { id, password });

            return callback && callback(isValid, RESPONSE_CODES[message] || RESPONSE_CODES.UNKNOWN);
        } catch (error: any) {
            if (error.response) {
                const ERROR_CODE = (error as AxiosError<DefaultRes<boolean>>)
                    .response?.data?.message;

                return callback && callback(false, RESPONSE_CODES[ERROR_CODE!] || RESPONSE_CODES.UNKNOWN);
            }
            return callback && callback(false, String(error));
        }
    }

    public static async fetchSystemPermissions(
        callback: DefaultCallback,
        signal?: AbortSignal
    ): Promise<string[]> {
        try {
            const {
                data: { isValid, message, result },
            } = await api.get<
                DefaultRes<string[]>,
                AxiosResponse<DefaultRes<string[]>>
            >(`/auth/customer-list-permissions`, { signal });

            callback && callback(isValid, RESPONSE_CODES[message]);

            return result;
        } catch (error: any) {
            if (isRequestCanceled(error)) {
                return [];
            }
            if (error.response) {
                const ERROR_CODE = (error as AxiosError<DefaultRes<boolean>>)
                    .response?.data?.message;

                callback && callback(false, RESPONSE_CODES[ERROR_CODE!]);

                return [];
            }
            callback && callback(false, String(error));

            return [];
        }
    }

    public static async changePhone(phone: string, callback: DefaultCallback) {
        try {
            const {
                dispatch: { updateUser },
            } = AuthService;

            const {
                data: { isValid, result },
            } = await api.post<
                DefaultRes<SystemUserTypes.Model>,
                AxiosResponse<DefaultRes<SystemUserTypes.Model>>
            >(`/customerWebAppUser/changePhone`, { phone });

            if (isValid) {
                updateUser(result);
            }

            return (
                callback &&
                callback(isValid, 'Phone number succesfully changed.')
            );
        } catch (error: any) {
            if (error.response) {
                const ERROR_CODE = (error as AxiosError<DefaultRes<boolean>>)
                    .response?.data?.message;

                return callback && callback(false, RESPONSE_CODES[ERROR_CODE!]);
            }
            return callback && callback(false, String(error));
        }
    }

    public static async changeLocation(
        location: LocationAddress,
        type: 'home' | 'work',
        callback: DefaultCallback
    ) {
        try {
            const {
                dispatch: { updateUser },
            } = AuthService;

            const urlAction =
                type === 'home' ? 'changeHomeLocation' : 'changeWorkLocation';

            const {
                data: { isValid, result },
            } = await api.post<
                DefaultRes<LocationAddress>,
                AxiosResponse<DefaultRes<SystemUserTypes.Model>>
            >(`/customerWebAppUser/${urlAction}`, location);

            if (isValid) {
                updateUser(result);
            }

            return (
                callback && callback(isValid, 'Address succesfully changed.')
            );
        } catch (error: any) {
            if (error.response) {
                const ERROR_CODE = (error as AxiosError<DefaultRes<boolean>>)
                    .response?.data?.message;

                return callback && callback(false, RESPONSE_CODES[ERROR_CODE!]);
            }
            return callback && callback(false, String(error));
        }
    }

    // sendMessages sends a message to a list of user ids
    public static async sendSMS(
        message: string,
        userIds: string[],
        callback: DefaultCallback
    ) {
        const URI = 'sendMessages';
        const SUCCESS_MESSAGE = 'Messages succesfully sent.';

        try {
            const {
                data: { isValid},
            } = await api.post<
                DefaultRes<LocationAddress>,
                AxiosResponse<DefaultRes<SystemUserTypes.Model>>
            >(`/customerWebAppUser/${URI}`, { message, userIds });

            return callback && callback(isValid, SUCCESS_MESSAGE);
        } catch (error: any) {
            if (error.response) {
                const ERROR_CODE = (error as AxiosError<DefaultRes<boolean>>)
                    .response?.data?.message;

                return callback && callback(false, RESPONSE_CODES[ERROR_CODE!]);
            }
            return callback && callback(false, String(error));
        }
    }

    // sendSmsBroadcast sends a message to all users on a subscription
    public static async sendSmsBroadcast(
        message: string,
        callback: DefaultCallback
    ) {
        const URI = 'sendSmsBroadcast';
        const SUCCESS_MESSAGE = 'Messages succesfully sent.';

        try {
            const {
                data: { isValid},
            } = await api.post<
                DefaultRes<LocationAddress>,
                AxiosResponse<DefaultRes<SystemUserTypes.Model>>
            >(`/customerWebAppUser/${URI}`, { message });

            return callback && callback(isValid, SUCCESS_MESSAGE);
        } catch (error: any) {
            if (error.response) {
                const ERROR_CODE = (error as AxiosError<DefaultRes<boolean>>)
                    .response?.data?.message;

                return callback && callback(false, RESPONSE_CODES[ERROR_CODE!]);
            }
            return callback && callback(false, String(error));
        }
    }
}
